import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "../../css/cogat/cogat.css";
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useNavigate } from "react-router-dom";
import FAQCOGATs from "./FAQCOGATs";
import CogatExamples from "./CogatExamples";

export default function HomePage() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false); // State for mobile detection
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Adjust 768px to your breakpoint
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call once to set initial state

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="homepage-container">
      <Header />
      <section className="cogat-description-section">
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1 }}
    className="cogat-content"
  >
    {/* Left side: Text */}
    <div className="cogat-text">
      <h2 className="cogat-header">What is the Cognitive Abilities Test (CoGAT)?</h2>
      <p className="cogat-paragraph">
        The Cognitive Abilities Test (CoGAT) is designed to assess a student’s reasoning and problem-solving skills across verbal, quantitative, and nonverbal areas. Many school districts use this test for gifted and talented program placements. CoGAT measures a student’s cognitive development, helping educators identify strengths and areas for growth.
      </p>
    </div>

    {/* Right side: Image (visible only if not mobile) */}
    {!isMobile && (
      <div className="cogat-image-container">
        <img src="/cogat.PNG" alt="CoGAT" className="cogat-image" />
      </div>
    )}
  </motion.div>
</section>


     {/* Section: CoGAT Key Areas */}
<section className="cogat-areas-section">
  <div className="cogat-content">
   

    {/* Right - Key Areas Breakdown */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="cogat-text-container"
    >
      <h2 className="cogat-header">Key Areas Assessed in CoGAT</h2>
      <p className="cogat-paragraph">
        The CoGAT test is divided into three main areas, each with multiple subtests designed to assess different reasoning abilities.
      </p>

      {/* Verbal Battery */}
      <h3 className="cogat-subheader">🔠 Verbal Battery</h3>
      <div className="cogat-list">
        <motion.div className="cogat-item" whileHover={{ scale: 1.05 }}>
          <strong>Picture/Verbal Analogies:</strong> Identifies relationships between images using a 2×2 matrix.
        </motion.div>
        <motion.div className="cogat-item" whileHover={{ scale: 1.05 }}>
          <strong>Sentence Completion:</strong> Requires choosing the best picture that completes a sentence.
        </motion.div>
        <motion.div className="cogat-item" whileHover={{ scale: 1.05 }}>
          <strong>Picture/Verbal Classification:</strong> Selects the image that fits with a given group.
        </motion.div>
      </div>

      {/* Quantitative Battery */}
      <h3 className="cogat-subheader">🔢 Quantitative Battery</h3>
      <div className="cogat-list">
        <motion.div className="cogat-item" whileHover={{ scale: 1.05 }}>
          <strong>Number Analogies:</strong> Identifies patterns and relationships between numbers.
        </motion.div>
        <motion.div className="cogat-item" whileHover={{ scale: 1.05 }}>
          <strong>Number Puzzles:</strong> Solves puzzles using basic numerical logic.
        </motion.div>
        <motion.div className="cogat-item" whileHover={{ scale: 1.05 }}>
          <strong>Number Series:</strong> Recognizes patterns in bead strings to determine the next in the series.
        </motion.div>
      </div>

      {/* Non-Verbal Battery */}
      <h3 className="cogat-subheader">📐 Non-Verbal Battery</h3>
      <div className="cogat-list">
        <motion.div className="cogat-item" whileHover={{ scale: 1.05 }}>
          <strong>Figure Matrices:</strong> Uses spatial reasoning to determine figure relationships.
        </motion.div>
        <motion.div className="cogat-item" whileHover={{ scale: 1.05 }}>
          <strong>Paper Folding:</strong> Predicts the outcome of folding and cutting paper.
        </motion.div>
        <motion.div className="cogat-item" whileHover={{ scale: 1.05 }}>
          <strong>Figure Classification:</strong> Groups figures based on shared characteristics.
        </motion.div>
      </div>
    </motion.div>
  </div>
</section>


      {/* Section 3: IAAT Test Details */}
      <section className="iaat-test-details-section">
        <div className="iaat-content">
          {/* Test Overview - visible only if not mobile */}
          {!isMobile && (
            <>
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="iaat-header"
              >
                Test Overview
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="iaat-paragraph2"
              >
                The Cognitive Abilities Test (COGAT) measures a student's reasoning and problem-solving abilities in areas such as verbal, quantitative, and non-verbal reasoning, providing valuable insights for academic placement and development.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="iaat-test-info"
              >
                <p><strong>Test Duration:</strong> 60-75 minutes</p>
                <p><strong>Format:</strong> Typically paper-based</p>
                <p><strong>Scoring:</strong> Scored based on standard norms for comparison</p>
              </motion.div>
            </>
          )}
        </div>
      </section>

      <CogatExamples />
      <FAQCOGATs />
      {/* Footer */}
      <Footer />
    </div>
  );
}
